const STORAGE_KEY = "caller.verification";

type StoredVerification = {
  callerVerificationToken: string;
  phoneNumber: string;
  expiresAt?: number;
};

/**
 * "Remember me" decides the storage, not just the expiry: without it the token
 * belongs in sessionStorage so it dies with the tab, which is what a caller on
 * a shared device is choosing.
 */
export function saveVerification(data: {
  callerVerificationToken: string;
  phoneNumber: string;
  expiresAt: string;
  rememberMe: boolean;
}): void {
  const { callerVerificationToken, phoneNumber, expiresAt, rememberMe } = data;
  const stored: StoredVerification = { callerVerificationToken, phoneNumber };

  clearVerification();

  if (rememberMe) {
    // The server's expiry, so there is one lifetime rather than a copy here
    // that has to be kept in step with it. Checking it only stops us sending a
    // token we already know is stale; the server validates its own.
    stored.expiresAt = new Date(expiresAt).getTime();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } else {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }
}

export function clearVerification(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

function read(storage: Storage): StoredVerification | null {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let stored: StoredVerification;
  try {
    stored = JSON.parse(raw);
  } catch (_) {
    // Anything unparseable is treated as absent rather than crashing the app on
    // boot; the caller just verifies again.
    storage.removeItem(STORAGE_KEY);
    return null;
  }

  if (!stored?.callerVerificationToken) {
    storage.removeItem(STORAGE_KEY);
    return null;
  }

  if (stored.expiresAt && stored.expiresAt < Date.now()) {
    storage.removeItem(STORAGE_KEY);
    return null;
  }

  return stored;
}

/**
 * sessionStorage wins: a token saved for this tab only is the more deliberate
 * choice, and should not be shadowed by an older remembered one.
 */
export function loadVerification(): StoredVerification | null {
  return read(sessionStorage) ?? read(localStorage);
}
